import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Loader from '../../components/Loader';
import ImageComponent from '../../components/ImageComponent';
import WithdrawText from '../Withdraw/components/WithdrawText';

import usePayoutStatus from '../../api/usePayoutStatus';
import { useWithdrawStore, resetWithdrawStore } from '../../storage/withdrawState';
import { CLEVERTAP_EVENTS } from '../../constant';
import { pushClevertapEvent } from '../../utils/analytics/clevertap';
import { cancelWithdrawStatusRefresh } from '../../utils/withdrawStatusRefresh';
import type { IStatusScreen } from '../../interfaces/Withdrawal';
import {
  WITHDRAW_STATUS_CTA_ENABLE_DELAY,
  getCtaLabel,
  getCtaRoute,
  getWithdrawStatusKey,
  getWithdrawStatusState,
  isTerminalStatus,
  shouldShowTransactionDetails,
  withPendingStatus,
} from './helper';
import StepIcon from './StepIcon';
import useProcessingCountdown, {
  useDeadlineCountdown,
} from './useProcessingCountdown';
import { formatFromNow } from '../../utils/DateUtils';
import styles from './styles.module.css';

/**
 * Withdrawal status screen.
 *
 * `processWithdrawal` already returns the first `statusScreen`, handed over via
 * route state, so the screen paints immediately. Updates arrive over the socket
 * (`REFRESH_WITHDRAW_STATUS_SCREEN` → `refreshWithdrawStatus`), which invalidates
 * this query. There is no polling and no manual refresh, matching Android, whose
 * status screen offers neither.
 *
 * `progress` does not wait for any of that: a countdown advances it to `pending`
 * on its own, the way `WithdrawStatusFragment.setCountdown` does.
 */
const WithdrawStatus: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { sessionId } = useWithdrawStore();
  const initialScreen = (location.state as { statusScreen?: IStatusScreen })
    ?.statusScreen;

  const { statusScreen, isLoading, error, refetch } = usePayoutStatus({
    sessionId,
    initialStatusScreen: initialScreen,
  });

  const serverScreen = statusScreen ?? initialScreen;

  /**
   * Set when the countdown has run out. Cleared whenever the server sends a new
   * screen, so a real status always outranks the local guess — Android resets
   * `isTransitioningToPending` on every emission.
   */
  const [pendingOverride, setPendingOverride] = useState(false);
  useEffect(() => setPendingOverride(false), [serverScreen]);

  const screen = useMemo(
    () =>
      pendingOverride && serverScreen
        ? withPendingStatus(serverScreen)
        : serverScreen,
    [pendingOverride, serverScreen],
  );

  const status = getWithdrawStatusKey(screen);
  const state = useMemo(() => getWithdrawStatusState(screen), [screen]);
  const detailRows = shouldShowTransactionDetails(status)
    ? screen?.transactionDetails?.withdraw?.details ?? []
    : [];
  const steps = screen?.transactionDetails?.withdraw?.steps ?? [];

  const isProgress = status === 'progress';

  const handleCountdownExpired = useCallback(() => setPendingOverride(true), []);
  const secondsLeft = useProcessingCountdown({
    active: isProgress,
    seconds: state?.processingDelay,
    onExpire: handleCountdownExpired,
  });

  /**
   * The CTA is withheld for a beat during `progress` so the user does not leave
   * before the screen has resolved — `WithdrawStatusFragment.setCtaData`.
   */
  const [ctaEnabled, setCtaEnabled] = useState(!isProgress);
  useEffect(() => {
    if (!isProgress) {
      setCtaEnabled(true);
      return;
    }

    setCtaEnabled(false);
    const timer = setTimeout(
      () => setCtaEnabled(true),
      screen?.ctaEnableDelay ?? WITHDRAW_STATUS_CTA_ENABLE_DELAY,
    );
    return () => clearTimeout(timer);
  }, [isProgress, screen?.ctaEnableDelay]);

  useEffect(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_STATUS_SCREEN, {
      status,
    });
  }, [status]);

  useEffect(() => () => cancelWithdrawStatusRefresh(), []);

  /**
   * The estimate the server pairs with `estimatedProcessingText`, whose copy ends
   * in "in ". Hidden once the payout is settled — the field is top-level, so the
   * server keeps sending it on the success screen too.
   */
  const isSettled = isTerminalStatus(status);
  const processingMsLeft = useDeadlineCountdown({
    deadline: screen?.estimatedProcessingTs,
    active: !isSettled,
    // The estimate has run out and we still have no terminal status — ask.
    onExpire: refetch,
  });

  const handleDone = () => {
    // The session is spent — clear it so a later visit opens a fresh one.
    resetWithdrawStore();
    navigate(getCtaRoute(screen?.cta));
  };

  if (isLoading && !screen) return <Loader />;

  if (!screen) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusCard}>
          <p className="fontSizeSm14">
            {(error as Error)?.message || t('errorOccurred')}
          </p>
          <button
            type="button"
            className="btn primaryBtn mdHeight"
            onClick={() => refetch()}
          >
            {t('retryLabel')}
          </button>
          <button
            type="button"
            className="btn secondaryBtn mdHeight"
            onClick={handleDone}
          >
            {t('goToWallet')}
          </button>
        </div>
      </div>
    );
  }

  const assetUrl = state?.assetUrl || screen.assetUrl;

  return (
    <div
      className={styles.statusPage}
      style={screen.bgColor ? { background: screen.bgColor } : undefined}
    >
      <div className={styles.statusCard}>
        {assetUrl && (
          <ImageComponent
            src={assetUrl}
            fallbackSrc={state?.fallbackAssetUrl}
            alt=""
            className={styles.statusAsset}
          />
        )}

        <WithdrawText
          data={state?.amount}
          className={clsx(styles.statusAmount, 'fontWeightBold')}
        />
        <WithdrawText
          data={state?.title}
          className={clsx(styles.statusTitle, 'fontWeightBold fontSizeSm')}
        />
        <WithdrawText
          data={state?.subTitle}
          className={clsx(styles.statusSubtitle, 'fontSizeSm14 fontColor666')}
        />

        {/* Sentence and estimate on one line: the copy ends in "in " and the
            value is appended, as Android packs `tvTimer` against `tvTimerText`.
            Gone once settled — the server sends this on success too. */}
        {!isSettled && screen.estimatedProcessingText && (
          <span className={styles.processingRow}>
            <WithdrawText
              data={screen.estimatedProcessingText}
              className="fontSizeSm12 fontColor666"
            />
            {processingMsLeft !== undefined && (
              <span className={clsx(styles.processingValue, 'fontSizeSm12')}>
                {formatFromNow(screen.estimatedProcessingTs as number)}
              </span>
            )}
          </span>
        )}

        {/* Android shows the same remainder in `tvTimer` while it waits. */}
        {isProgress && secondsLeft !== undefined && secondsLeft >= 0 && (
          <span className={clsx(styles.statusTimer, 'fontSizeSm14 fontColor666')}>
            {t('secondsLeft', { seconds: secondsLeft })}
          </span>
        )}

        {/* A live PAYOUT response carries `details` and no `steps`; both are
            rendered because Android types both. */}
        {(detailRows.length > 0 || steps.length > 0) && (
          <div className={styles.steps}>
            <WithdrawText
              data={screen.transactionDetails?.headerText}
              className={clsx(styles.stepsHeader, 'fontWeightBold fontSizeSm14')}
            />

            {detailRows.map((row, index) => (
              <div className={styles.detailRow} key={index}>
                <span className="fontSizeSm12 fontColor666">
                  {row.displayText}
                </span>
                <span className={clsx(styles.detailValue, 'fontSizeSm12')}>
                  {row.value}
                </span>
              </div>
            ))}

            {steps.map((step, index) => (
              <div className={styles.step} key={index}>
                {/* Always rendered: an absent icon used to collapse the slot and
                    shunt the whole row left. */}
                <StepIcon
                  assetUrl={step.assetUrl1}
                  index={index}
                  status={status}
                  className={styles.stepIcon}
                />
                <div className={styles.stepBody}>
                  <WithdrawText data={step.title} className="fontSizeSm14" />
                  <WithdrawText
                    data={step.subtitle}
                    className="fontSizeSm12 fontColor666"
                  />
                </div>
                <WithdrawText
                  data={step.time}
                  className="fontSizeSm12 fontColor666"
                />
              </div>
            ))}
          </div>
        )}

        <WithdrawText
          data={screen.footer}
          className={clsx(styles.statusFooter, 'fontSizeSm12 fontColor666')}
        />
      </div>

      <div className={styles.statusActions}>
        <button
          type="button"
          className="btn primaryBtn"
          onClick={handleDone}
          disabled={!ctaEnabled}
        >
          {getCtaLabel(screen.cta) || t('goToWallet')}
        </button>

        {/* No manual refresh: the socket pushes every update, and Android's
            status screen offers no refresh control either. */}
      </div>
    </div>
  );
};

export default WithdrawStatus;
